import React, { useState, useEffect, useRef } from 'react';
import { ActionItem } from './types';

interface TranscriptWithClients {
  id: string;
  title: string;
  created_at: string;
  clients: string[]; // Array of client names
}

interface TaskCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Partial<ActionItem>) => Promise<void>;
}

const TaskCreateModal: React.FC<TaskCreateModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave 
}) => {
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as ActionItem['priority'],
    status: 'pending' as ActionItem['status'],
    assignee: '',
    category: '',
    dueDate: '',
    notes: '',
    transcriptId: '' // Empty means no transcript
  });

  // Transcript selection state
  const [transcripts, setTranscripts] = useState<TranscriptWithClients[]>([]);
  const [filteredTranscripts, setFilteredTranscripts] = useState<TranscriptWithClients[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptWithClients | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [hasMoreTranscripts, setHasMoreTranscripts] = useState(false);
  const [transcriptsOffset, setTranscriptsOffset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load transcripts with client information
  const loadTranscripts = async (search = '', offset = 0, append = false) => {
    setLoadingTranscripts(true);
    try {
      // First get transcripts
      const transcriptsResponse = await fetch(`/api/transcripts/list?limit=20&offset=${offset}&search=${encodeURIComponent(search)}`, {
        credentials: 'include'
      });
      
      if (transcriptsResponse.ok) {
        const transcriptsData = await transcriptsResponse.json() as { 
          transcripts: Array<{id: string, title: string, created_at: string}>,
          pagination: { hasMore: boolean }
        };
        
        // Get client information for each transcript
        const transcriptsWithClients = await Promise.all(
          transcriptsData.transcripts.map(async (transcript) => {
            try {
              const clientsResponse = await fetch(`/api/client-transcripts/list?transcriptId=${transcript.id}`, {
                credentials: 'include'
              });
              
              if (clientsResponse.ok) {
                const clientsData = await clientsResponse.json() as {
                  clientTranscripts: Array<{
                    client_first_name: string,
                    client_last_name: string
                  }>
                };
                
                const clients = clientsData.clientTranscripts.map(ct => 
                  `${ct.client_first_name} ${ct.client_last_name}`.trim()
                );
                
                return {
                  ...transcript,
                  clients
                };
              }
              
              return {
                ...transcript,
                clients: []
              };
            } catch (error) {
              console.error(`Error loading clients for transcript ${transcript.id}:`, error);
              return {
                ...transcript,
                clients: []
              };
            }
          })
        );
        
        if (append) {
          setTranscripts(prev => [...prev, ...transcriptsWithClients]);
          setFilteredTranscripts(prev => [...prev, ...transcriptsWithClients]);
        } else {
          setTranscripts(transcriptsWithClients);
          setFilteredTranscripts(transcriptsWithClients);
        }
        
        setHasMoreTranscripts(transcriptsData.pagination.hasMore);
        setTranscriptsOffset(offset + 20);
      }
    } catch (error) {
      console.error('Error loading transcripts:', error);
    } finally {
      setLoadingTranscripts(false);
    }
  };

  // Search transcripts
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      loadTranscripts(searchTerm, 0, false);
    }, 300); // Debounce search
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen]);

  // Load more transcripts on scroll
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 5 && hasMoreTranscripts && !loadingTranscripts) {
      loadTranscripts(searchTerm, transcriptsOffset, true);
    }
  };

  // Handle transcript selection
  const handleTranscriptSelect = (transcript: TranscriptWithClients | null) => {
    setSelectedTranscript(transcript);
    setFormData(prev => ({ ...prev, transcriptId: transcript?.id || '' }));
    setShowDropdown(false);
    setSearchTerm(transcript ? getTranscriptDisplayText(transcript) : '');
  };

  // Format transcript display text
  const getTranscriptDisplayText = (transcript: TranscriptWithClients) => {
    const clientsText = transcript.clients.length > 0 ? ` - ${transcript.clients.join(', ')}` : ' - No clients';
    return `${transcript.title}${clientsText}`;
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: 'pending',
        assignee: '',
        category: '',
        dueDate: '',
        notes: '',
        transcriptId: ''
      });
      setSearchTerm('');
      setSelectedTranscript(null);
      setShowDropdown(false);
      setTranscriptsOffset(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Note: For now, we need a transcript ID due to schema constraints
      const transcriptIdToUse = formData.transcriptId || null;
      
      if (!transcriptIdToUse) {
        alert('Please select a transcript. Standalone tasks will be supported in a future update.');
        return;
      }

      const taskData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        assignee: formData.assignee || null,
        category: formData.category || 'general',
        due_date: formData.dueDate || null,
        notes: formData.notes || null,
        transcript_id: transcriptIdToUse
      };

      await onSave(taskData);
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Add New Task</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div 
          className="flex-1 overflow-y-scroll min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ 
            maxHeight: 'calc(-80px + 90vh)', 
            scrollbarWidth: 'thin', 
            scrollbarColor: 'rgb(209, 213, 219) rgb(243, 244, 246)' 
          }}
        >
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Transcript Selection */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link to Transcript <span className="text-gray-500">(Optional - Select a transcript to associate this task with)</span>
                </label>
                
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                    placeholder="Search transcripts or leave empty for standalone task..."
                  />
                  
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Dropdown */}
                {showDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                    {/* No transcript option */}
                    <div
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      onClick={() => handleTranscriptSelect(null)}
                    >
                      <div className="text-sm text-gray-600">No transcript (Standalone task)</div>
                    </div>
                    
                    {/* Loading state */}
                    {loadingTranscripts && transcripts.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">Loading transcripts...</div>
                    )}
                    
                    {/* Transcript options */}
                    <div onScroll={handleScroll} className="max-h-48 overflow-y-auto">
                      {filteredTranscripts.map((transcript) => (
                        <div
                          key={transcript.id}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
                          onClick={() => handleTranscriptSelect(transcript)}
                        >
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {transcript.title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {transcript.clients.length > 0 ? (
                              <span>Clients: {transcript.clients.join(', ')}</span>
                            ) : (
                              <span>No clients attached</span>
                            )}
                            <span className="ml-2">• {new Date(transcript.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                      
                      {/* Load more indicator */}
                      {loadingTranscripts && transcripts.length > 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">Loading more...</div>
                      )}
                      
                      {/* No results */}
                      {!loadingTranscripts && filteredTranscripts.length === 0 && searchTerm && (
                        <div className="px-3 py-2 text-sm text-gray-500">No transcripts found matching "{searchTerm}"</div>
                      )}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-amber-600 mt-1">
                  Note: Due to current schema constraints, selecting a transcript is currently required. Standalone tasks will be supported in a future update.
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Priority and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value as ActionItem['priority']})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as ActionItem['status']})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Category and Assignee */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., follow-up, research, meeting"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                  <input
                    type="text"
                    value={formData.assignee}
                    onChange={(e) => setFormData({...formData, assignee: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Who should handle this task?"
                  />
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional notes or context..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.title.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCreateModal; 